import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PermissionGate } from "../../components/auth/PermissionGate";
import { EmptyState } from "../../components/common/EmptyState";
import { TableSkeletonRows } from "../../components/common/LoadingSkeletons";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { showToast } from "../../lib/toast";
import { Product, productService } from "../../services/product.service";

interface ProductStats {
  totalProducts: number;
  activeProducts: number;
  lowStockCount: number;
  expiringCount: number;
}

type SortField =
  | "name"
  | "sku"
  | "category"
  | "sellingPrice"
  | "scheduleType"
  | "createdAt";
type SortOrder = "asc" | "desc";

const SCHEDULE_TYPES = [
  { value: "", label: "All Schedules" },
  { value: "GENERAL", label: "General" },
  { value: "SCHEDULE_G", label: "Schedule G" },
  { value: "SCHEDULE_H", label: "Schedule H" },
  { value: "SCHEDULE_H1", label: "Schedule H1" },
  { value: "SCHEDULE_X", label: "Schedule X" },
];

const PAGE_SIZES = [20, 50, 100];

const SCHEDULE_BADGE_CLASSES: Record<string, string> = {
  SCHEDULE_X: "bg-purple-100 text-purple-800",
  SCHEDULE_H1: "bg-red-100 text-red-800",
  SCHEDULE_H: "bg-orange-100 text-orange-800",
  SCHEDULE_G: "bg-yellow-100 text-yellow-800",
  GENERAL: "bg-gray-100 text-gray-800",
};

function formatScheduleLabel(type: string) {
  return type.replace(/_/g, " ");
}

export function ProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [scheduleFilter, setScheduleFilter] = useState("");
  const [categorySuggestions, setCategorySuggestions] = useState<string[]>([]);

  // Sorting
  const [sortBy, setSortBy] = useState<SortField>("name");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });

  // Stats
  const [stats, setStats] = useState<ProductStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Selection & Bulk Actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Delete Modal
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    id: string;
    name: string;
    isBulk: boolean;
  }>({
    isOpen: false,
    id: "",
    name: "",
    isBulk: false,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  // Export
  const [isExporting, setIsExporting] = useState(false);

  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const data = await productService.getProductStats();
      setStats(data);
    } catch {
      // stats are non-critical
    } finally {
      setStatsLoading(false);
    }
  };

  const loadCategorySuggestions = async () => {
    try {
      const data = await productService.getCategorySuggestions();
      setCategorySuggestions(data.categories || []);
    } catch {
      // non-critical
    }
  };

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await productService.getProducts({
        page,
        limit: pageSize,
        search: search || undefined,
        category: categoryFilter || undefined,
        scheduleType: scheduleFilter || undefined,
        sortBy,
        sortOrder,
      });
      setProducts(response.data);
      setPagination(response.pagination);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load products");
    } finally {
      setLoading(false);
    }
  }, [
    page,
    pageSize,
    search,
    categoryFilter,
    scheduleFilter,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    loadStats();
    loadCategorySuggestions();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // Clear selection when products change
  useEffect(() => {
    setSelectedIds(new Set());
    setSelectAll(false);
  }, [products]);

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortBy !== field) return <span className="text-gray-300 ml-1">↕</span>;
    return (
      <span className="text-blue-600 ml-1">
        {sortOrder === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  const clearFilters = () => {
    setSearch("");
    setCategoryFilter("");
    setScheduleFilter("");
    setPage(1);
  };

  const hasActiveFilters = search || categoryFilter || scheduleFilter;

  // Selection handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
      setSelectAll(false);
    } else {
      setSelectedIds(new Set(products.map((p) => p.id)));
      setSelectAll(true);
    }
  };

  const handleSelectProduct = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectedCount = selectedIds.size;

  // Delete handlers
  const handleDeleteClick = (id: string, name: string) => {
    setDeleteModal({ isOpen: true, id, name, isBulk: false });
  };

  const handleBulkDeleteClick = () => {
    setDeleteModal({
      isOpen: true,
      id: "",
      name: `${selectedCount} selected products`,
      isBulk: true,
    });
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      if (deleteModal.isBulk) {
        const result = await productService.bulkDeleteProducts(
          Array.from(selectedIds),
        );
        showToast.success(`${result.deletedCount} products deleted`);
        setSelectedIds(new Set());
      } else {
        await productService.deleteProduct(deleteModal.id);
        showToast.success("Product deleted successfully");
      }
      loadProducts();
      loadStats();
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to delete product(s)",
      );
    } finally {
      setIsDeleting(false);
      setDeleteModal({ isOpen: false, id: "", name: "", isBulk: false });
    }
  };

  // Export handler
  const handleExport = async () => {
    try {
      setIsExporting(true);
      await productService.exportProductsCSV({
        search: search || undefined,
        category: categoryFilter || undefined,
        scheduleType: scheduleFilter || undefined,
      });
      showToast.success("Products exported successfully");
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to export products",
      );
    } finally {
      setIsExporting(false);
    }
  };

  const statCards = useMemo(() => {
    if (!stats) return [];
    return [
      {
        label: "Total Products",
        value: stats.totalProducts,
        icon: "📦",
        color: "bg-blue-50 border-blue-200 text-blue-700",
      },
      {
        label: "Active",
        value: stats.activeProducts,
        icon: "✅",
        color: "bg-green-50 border-green-200 text-green-700",
      },
      {
        label: "Low Stock",
        value: stats.lowStockCount,
        icon: "⚠️",
        color: "bg-amber-50 border-amber-200 text-amber-700",
      },
      {
        label: "Expiring Soon",
        value: stats.expiringCount,
        icon: "⏰",
        color: "bg-red-50 border-red-200 text-red-700",
      },
    ];
  }, [stats]);

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage your product catalog and inventory
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {isExporting ? "Exporting..." : "📤 Export CSV"}
          </button>
          <Link
            to="/products/import"
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            📥 Import CSV
          </Link>
          <PermissionGate permission="products:create">
            <Link
              to="/products/new"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              + Add Product
            </Link>
          </PermissionGate>
        </div>
      </div>

      {/* Stats Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statsLoading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse"
              >
                <div className="h-4 w-20 bg-gray-200 rounded mb-2" />
                <div className="h-7 w-12 bg-gray-200 rounded" />
              </div>
            ))
          : statCards.map((stat) => (
              <div
                key={stat.label}
                className={`border rounded-xl p-4 ${stat.color}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{stat.label}</span>
                  <span className="text-lg">{stat.icon}</span>
                </div>
                <div className="text-2xl font-bold mt-1">
                  {stat.value.toLocaleString()}
                </div>
              </div>
            ))}
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-3">
        <form onSubmit={handleSearch} className="flex gap-3 flex-wrap">
          <div className="flex-1 min-w-[250px] relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              🔍
            </span>
            <input
              type="text"
              placeholder="Search by name, SKU, barcode, or manufacturer..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[160px]"
          >
            <option value="">All Categories</option>
            {categorySuggestions.map((cat, idx) => (
              <option key={idx} value={cat}>
                {cat}
              </option>
            ))}
          </select>
          <select
            value={scheduleFilter}
            onChange={(e) => {
              setScheduleFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[160px]"
          >
            {SCHEDULE_TYPES.map((st) => (
              <option key={st.value} value={st.value}>
                {st.label}
              </option>
            ))}
          </select>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="px-4 py-2.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              ✕ Clear
            </button>
          )}
        </form>

        {/* Bulk Actions Bar */}
        {selectedCount > 0 && (
          <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5">
            <span className="text-sm font-medium text-blue-800">
              {selectedCount} product{selectedCount !== 1 ? "s" : ""} selected
            </span>
            <div className="flex-1" />
            <PermissionGate permission="products:delete">
              <button
                onClick={handleBulkDeleteClick}
                className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                🗑 Delete Selected
              </button>
            </PermissionGate>
            <button
              onClick={() => {
                setSelectedIds(new Set());
                setSelectAll(false);
              }}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-white transition-colors"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Products Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {[
                  "",
                  "Product",
                  "SKU",
                  "Category",
                  "Stock",
                  "Price",
                  "Schedule",
                  "Actions",
                ].map((h) => (
                  <th key={h} className="px-4 py-3">
                    <div className="h-3 w-16 rounded bg-slate-200 animate-pulse" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <TableSkeletonRows rows={8} cols={8} />
            </tbody>
          </table>
        ) : products.length === 0 ? (
          <EmptyState
            icon="box"
            title="No products found"
            description={
              hasActiveFilters
                ? "Try adjusting your filters or search term."
                : "Get started by adding your first product to the inventory."
            }
            action={
              hasActiveFilters
                ? { label: "Clear filters", onClick: clearFilters }
                : {
                    label: "Create product",
                    onClick: () => (window.location.href = "/products/new"),
                  }
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort("name")}
                    >
                      Product <SortIcon field="name" />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort("sku")}
                    >
                      SKU <SortIcon field="sku" />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort("category")}
                    >
                      Category <SortIcon field="category" />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stock
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort("sellingPrice")}
                    >
                      Price <SortIcon field="sellingPrice" />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort("scheduleType")}
                    >
                      Schedule <SortIcon field="scheduleType" />
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {products.map((product) => {
                    const stock = product.totalStock || 0;
                    const isSelected = selectedIds.has(product.id);
                    return (
                      <tr
                        key={product.id}
                        className={`hover:bg-gray-50 transition-colors ${isSelected ? "bg-blue-50" : ""}`}
                      >
                        <td className="px-4 py-4">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSelectProduct(product.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <Link
                            to={`/products/${product.id}`}
                            className="block"
                          >
                            <div className="text-sm font-medium text-gray-900 hover:text-blue-600 transition-colors">
                              {product.name}
                            </div>
                            {product.manufacturer && (
                              <div className="text-xs text-gray-500 mt-0.5">
                                {product.manufacturer}
                              </div>
                            )}
                          </Link>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-900 font-mono">
                            {product.sku}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div>
                            <div>{product.category || "—"}</div>
                            {product.subcategory && (
                              <div className="text-xs text-gray-500">
                                {product.subcategory}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span
                              className={`inline-flex items-center gap-1 text-sm font-medium ${
                                stock > 10
                                  ? "text-green-700"
                                  : stock > 0
                                    ? "text-amber-600"
                                    : "text-red-600"
                              }`}
                            >
                              {stock === 0 && (
                                <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
                              )}
                              {stock} {product.unit}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                          ₹{product.sellingPrice}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                              SCHEDULE_BADGE_CLASSES[product.scheduleType] ||
                              "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {formatScheduleLabel(product.scheduleType)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right text-sm space-x-3">
                          <Link
                            to={`/products/${product.id}`}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            View
                          </Link>
                          <PermissionGate permission="products:update">
                            <Link
                              to={`/products/${product.id}/edit`}
                              className="text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                              Edit
                            </Link>
                          </PermissionGate>
                          <PermissionGate permission="products:delete">
                            <button
                              onClick={() =>
                                handleDeleteClick(product.id, product.name)
                              }
                              className="text-red-600 hover:text-red-800 font-medium"
                            >
                              Delete
                            </button>
                          </PermissionGate>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-700">
                  Showing{" "}
                  <span className="font-medium">
                    {(pagination.page - 1) * pagination.limit + 1}
                  </span>
                  –
                  <span className="font-medium">
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.total,
                    )}
                  </span>{" "}
                  of{" "}
                  <span className="font-medium">
                    {pagination.total.toLocaleString()}
                  </span>
                </span>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Per page:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                    className="border border-gray-300 rounded-md text-sm py-1 px-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {PAGE_SIZES.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  First
                </button>
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  ← Prev
                </button>
                <span className="px-3 py-1.5 text-sm text-gray-600">
                  Page {page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === pagination.totalPages}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next →
                </button>
                <button
                  onClick={() => setPage(pagination.totalPages)}
                  disabled={page === pagination.totalPages}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Last
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({ isOpen: false, id: "", name: "", isBulk: false })
        }
        onConfirm={handleDeleteConfirm}
        title={deleteModal.isBulk ? "Delete Products" : "Delete Product"}
        message={
          deleteModal.isBulk
            ? `Are you sure you want to delete ${selectedCount} selected products? This action cannot be undone.`
            : `Are you sure you want to delete "${deleteModal.name}"? This action cannot be undone.`
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}
